'use client';

import { useState } from 'react';
import { useRouter } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';
import { trackEvent } from '@/lib/analytics';

interface HotelSearchFormProps {
  onSearch: (params: any) => void;
}

const HotelSearchForm = ({ onSearch }: HotelSearchFormProps) => {
  const router = useRouter();
  const [destination, setDestination] = useState('');
  const [checkIn, setCheckIn] = useState('');
  const [checkOut, setCheckOut] = useState('');
  const [guests, setGuests] = useState(2);
  const [rooms, setRooms] = useState(1);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const searchParams = { destination, checkIn, checkOut, guests, rooms };
    
    trackEvent('hotel_search', {
      destination,
      guests,
      rooms,
      nights: checkIn && checkOut ? Math.ceil((new Date(checkOut).getTime() - new Date(checkIn).getTime()) / (1000 * 60 * 60 * 24)) : 0,
    });
    
    onSearch(searchParams);
  };

  return (
    <div className="bg-card rounded-xl shadow-3d p-6 lg:p-8 transform-3d shine-effect border-4 border-accent/20">
      <form onSubmit={handleSubmit}>
        <div className="space-y-4">
          {/* Destination */}
          <div className="relative">
            <label className="block text-sm font-black text-foreground mb-2">
              Destination
            </label>
            <div className="relative">
              <Icon name="MapPinIcon" size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
              <input
                type="text"
                value={destination}
                onChange={(e) => setDestination(e.target.value)}
                placeholder="City, hotel name, or landmark"
                className="w-full pl-10 pr-4 py-3 border-2 border-border rounded-lg focus:ring-4 focus:ring-primary focus:border-transparent transition-all font-bold shadow-brand-lg text-foreground"
                required
              />
            </div>
          </div>

          {/* Dates Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-black text-foreground mb-2">
                Check-in
              </label>
              <div className="relative">
                <Icon name="CalendarIcon" size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                <input
                  type="date"
                  value={checkIn}
                  onChange={(e) => setCheckIn(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border-2 border-border rounded-lg focus:ring-4 focus:ring-primary focus:border-transparent transition-all font-bold shadow-brand-lg text-foreground"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-black text-foreground mb-2">
                Check-out
              </label>
              <div className="relative">
                <Icon name="CalendarIcon" size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                <input
                  type="date"
                  value={checkOut}
                  onChange={(e) => setCheckOut(e.target.value)}
                  className="w-full pl-10 pr-4 py-3 border-2 border-border rounded-lg focus:ring-4 focus:ring-primary focus:border-transparent transition-all font-bold shadow-brand-lg text-foreground"
                  required
                />
              </div>
            </div>
          </div>

          {/* Guests and Rooms */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="relative">
              <label className="block text-sm font-black text-foreground mb-2">
                Guests
              </label>
              <div className="relative">
                <Icon name="UserGroupIcon" size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                <select
                  value={guests}
                  onChange={(e) => setGuests(Number(e.target.value))}
                  className="w-full pl-10 pr-4 py-3 border-2 border-border rounded-lg focus:ring-4 focus:ring-primary focus:border-transparent transition-all font-bold shadow-brand-lg appearance-none text-foreground"
                >
                  {[1, 2, 3, 4, 5, 6, 7, 8].map(num => (
                    <option key={num} value={num}>{num} Guest{num > 1 ? 's' : ''}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="relative">
              <label className="block text-sm font-black text-foreground mb-2">
                Rooms
              </label>
              <div className="relative">
                <Icon name="HomeIcon" size={20} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
                <select
                  value={rooms}
                  onChange={(e) => setRooms(Number(e.target.value))}
                  className="w-full pl-10 pr-4 py-3 border-2 border-border rounded-lg focus:ring-4 focus:ring-primary focus:border-transparent transition-all font-bold shadow-brand-lg appearance-none text-foreground"
                >
                  {[1, 2, 3, 4, 5].map(num => (
                    <option key={num} value={num}>{num} Room{num > 1 ? 's' : ''}</option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Search Button */}
          <button
            type="submit"
            className="w-full px-8 py-4 gradient-gold-shiny text-primary font-heading font-black text-lg rounded-lg shadow-3d transition-all duration-300 hover:scale-105 active:scale-95 shine-effect border-2 border-accent flex items-center justify-center gap-2"
          >
            <Icon name="MagnifyingGlassIcon" size={22} />
            Search Hotels
          </button>
        </div>
      </form>
    </div>
  );
};

export default HotelSearchForm;